import { useMemo } from 'react';
import { useAggregationLevelContext } from 'src/features/MM/contexts/AggregationLevelContext';
import { useSelectedDimensionContext } from 'src/features/MM/contexts/SelectedDimensionContext';
import { useByDimensionNameAndId } from 'src/features/MM/hooks/useByDimensionId';
import { Dimension, MMDataState } from 'src/features/MM/types';
import { getMMDataStateAggregateLevelData } from 'src/features/MM/utils/getAggregateLevelData';
import { getDimensionLevelTimeframeData } from 'src/features/MM/utils/getDimensionLevelData';

export const TOP_N_CAMPAIGNS = 15;
export const OTHER_LABEL = 'Other';

export type TrendsChartDataRow = {
  utcEpoch: number;
} & Record<string, number>;

export const useChartData = (
  metricState: MMDataState,
  shouldDataBePercent: boolean,
  totalLineLabel?: string,
): { legendKeys: Array<string>; data: Array<TrendsChartDataRow> } => {
  const { aggregateLevel } = useAggregationLevelContext();
  const { dimension } = useSelectedDimensionContext();

  const byDimensionNameAndId = useByDimensionNameAndId();

  const isCampaignDimension = dimension === Dimension.campagin;

  const { topIds, hasOther } = useMemo(() => {
    if (!isCampaignDimension) {
      return {
        topIds: null as Set<string> | null,
        hasOther: false,
      };
    }
    if (byDimensionNameAndId.length <= TOP_N_CAMPAIGNS) {
      return {
        topIds: new Set(byDimensionNameAndId.map((r) => r.id)),
        hasOther: false,
      };
    }
    const ranked = [...byDimensionNameAndId]
      .map(({ id, name }) => ({
        id,
        name,
        total: Math.abs(metricState.totalByCampaignId[id] ?? 0),
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, TOP_N_CAMPAIGNS);
    return {
      topIds: new Set(ranked.map((r) => r.id)),
      hasOther: true,
    };
  }, [
    isCampaignDimension,
    byDimensionNameAndId,
    metricState.totalByCampaignId,
  ]);

  const data = useMemo(() => {
    const timeframeData = getMMDataStateAggregateLevelData(
      metricState,
      aggregateLevel,
    );

    const dataRows: Array<TrendsChartDataRow> = [];

    timeframeData.forEach((d) => {
      const total = d.value;
      const dimensionRecord = getDimensionLevelTimeframeData(d, dimension);

      const dataRow: TrendsChartDataRow = {
        utcEpoch: d.utcEpoch,
      };
      if (totalLineLabel) {
        dataRow[totalLineLabel] = total;
      }

      let otherValue = 0;
      byDimensionNameAndId.forEach(({ name, id }) => {
        const value = dimensionRecord[id] ?? 0;
        if (isCampaignDimension && topIds && !topIds.has(id)) {
          otherValue += value;
          return;
        }
        const percent = total ? (value * 100) / total : 0;
        dataRow[name] = shouldDataBePercent ? percent : value;
      });

      if (isCampaignDimension && hasOther) {
        const percent = total ? (otherValue * 100) / total : 0;
        dataRow[OTHER_LABEL] = shouldDataBePercent ? percent : otherValue;
      }

      dataRows.push(dataRow);
    });

    return dataRows;
  }, [
    dimension,
    byDimensionNameAndId,
    aggregateLevel,
    metricState,
    totalLineLabel,
    shouldDataBePercent,
    isCampaignDimension,
    topIds,
    hasOther,
  ]);

  const legendKeys = useMemo(() => {
    if (isCampaignDimension && topIds) {
      const survivors = byDimensionNameAndId
        .filter(({ id }) => topIds.has(id))
        .map(({ name }) => name);
      return hasOther ? [...survivors, OTHER_LABEL] : survivors;
    }
    return byDimensionNameAndId.map(({ name }) => name);
  }, [isCampaignDimension, topIds, hasOther, byDimensionNameAndId]);

  return {
    data,
    legendKeys,
  };
};
